// Basis Pengetahuan helpers for the admin console (docs/contract.md 4, 9.2):
// file name rules, system file labels, sizes, the validator summary, and the
// line and column inside an Indonesian parse error.
import type { SystemFileKind } from "../api";
import { formatCount } from "../lib/format";
import { KNOWLEDGE_CATEGORY_LABEL } from "../lib/labels";

export const SYSTEM_FILES: Record<string, { kind: SystemFileKind; label: string; unit: string; lead: string; manage?: { href: string; label: string } }> = {
  "_catalog.md": {
    kind: "catalog",
    label: "Katalog dokumen",
    unit: "jenis dokumen",
    lead: "Tahap, jenis dokumen, sub kategori, dan kolom formulir Buat Dokumen.",
  },
  "_faq.md": {
    kind: "faq",
    label: "Pertanyaan umum",
    unit: "pertanyaan",
    lead: "Pertanyaan cepat di Chat, dijawab langsung dari daftar jawaban tersimpan.",
  },
  "_elearning.md": {
    kind: "elearning",
    label: "E-Learning",
    unit: "kelompok video",
    lead: "Kelompok dan video panduan E-Learning. Paling mudah dikelola dari halaman E-Learning.",
    manage: { href: "/admin/elearning", label: "Kelola E-Learning" },
  },
};

export const SYSTEM_ORDER = ["_catalog.md", "_faq.md", "_elearning.md"];

export const MAX_BYTES = 2 * 1024 * 1024;

const FILE_NAME = /^[a-z0-9][a-z0-9-]{0,80}\.md$/;

export function isSystemFile(file: string): boolean {
  return file in SYSTEM_FILES;
}

/** The file name rule of contract 9.2; null when the name is allowed. */
export function fileNameError(file: string): string | null {
  const f = file.trim();
  if (!f) return "Nama berkas wajib diisi.";
  if (isSystemFile(f)) return null;
  if (/[\\/]/.test(f)) return "Nama berkas tidak boleh berisi garis miring.";
  if (!f.endsWith(".md")) return "Nama berkas harus diakhiri .md.";
  if (!FILE_NAME.test(f)) return "Gunakan huruf kecil, angka, dan tanda hubung saja, diawali huruf atau angka. Contoh: perpres-16-2018.md.";
  return null;
}

/** A picked file's name turned into an allowed one: "Perpres 16 2018.MD" to "perpres-16-2018.md". */
export function suggestFileName(name: string): string {
  const base = name
    .replace(/\.md$/i, "")
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
  return base ? `${base}.md` : "";
}

export function bytesOf(text: string): number {
  return new TextEncoder().encode(text).length;
}

/** 812 B, 1,8 KB, 2,0 MB (Indonesian decimal comma). */
export function formatSize(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || Number.isNaN(bytes)) return "Tidak diketahui";
  if (bytes < 1024) return `${formatCount(bytes)} B`;
  const fmt = new Intl.NumberFormat("id-ID", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  if (bytes < 1024 * 1024) return `${fmt.format(bytes / 1024)} KB`;
  return `${fmt.format(bytes / (1024 * 1024))} MB`;
}

export function lineCount(text: string): number {
  if (!text) return 0;
  return text.split("\n").length;
}

/** "baris 12, kolom 5" inside a parser message, or null. */
export function errorPosition(message: string): { line: number; column: number } | null {
  const m = /baris\s+(\d+)(?:\s*,\s*kolom\s+(\d+))?/i.exec(message);
  if (!m) return null;
  return { line: Number(m[1]), column: m[2] ? Number(m[2]) : 1 };
}

/** The character offset of a 1-based line and column in `text` (clamped). */
export function offsetOf(text: string, line: number, column: number): number {
  const lines = text.split("\n");
  const l = Math.min(Math.max(1, line), lines.length);
  let offset = 0;
  for (let i = 0; i < l - 1; i++) offset += lines[i]!.length + 1;
  return offset + Math.min(Math.max(0, column - 1), lines[l - 1]!.length);
}

const SUMMARY_LABEL: Record<string, string> = {
  kind: "Jenis",
  id: "Kode",
  title: "Judul",
  category: "Kategori",
  sectionCount: "Bagian",
  itemCount: "Jumlah butir",
  documentTypeCount: "Jenis dokumen",
  tahapCount: "Tahap",
  courseCount: "Kelompok video",
  lessonCount: "Video",
};

const KIND_LABEL: Record<string, string> = {
  knowledge: "Berkas pengetahuan",
  entry: "Berkas pengetahuan",
  catalog: "Katalog dokumen",
  faq: "Pertanyaan umum",
  elearning: "E-Learning",
};

/** The validator summary as label and value pairs; text stays as one line. */
export function summaryFacts(summary: unknown): { label: string; value: string }[] {
  if (typeof summary === "string") return summary.trim() ? [{ label: "Ringkasan", value: summary.trim() }] : [];
  if (!summary || typeof summary !== "object") return [];
  const out: { label: string; value: string }[] = [];
  for (const [key, label] of Object.entries(SUMMARY_LABEL)) {
    const v = (summary as Record<string, unknown>)[key];
    if (v === undefined || v === null || v === "") continue;
    if (key === "kind" && typeof v === "string") out.push({ label, value: KIND_LABEL[v] ?? v });
    else if (key === "category" && typeof v === "string") out.push({ label, value: KNOWLEDGE_CATEGORY_LABEL[v] ?? v });
    else if (typeof v === "number") out.push({ label, value: formatCount(v) });
    else if (typeof v === "string") out.push({ label, value: v });
  }
  return out;
}

/** The minimal valid knowledge file, from knowledge/README.md. */
export const MINIMAL_EXAMPLE = `# Panduan Penyusunan HPS

Catatan bebas di luar blok JSON diabaikan.

\`\`\`json
{
  "id": "panduan-hps",
  "title": "Panduan Penyusunan HPS",
  "category": "panduan",
  "tahap": ["persiapan_pengadaan"],
  "documentTypes": ["hps"],
  "sections": [
    { "id": "pengertian", "heading": "Pengertian HPS", "content": "Isi bagian, disalin dari dokumen resmi." }
  ]
}
\`\`\`
`;
